import { useTranslation } from 'react-i18next';
import Header from '../../home/components/Header';
import Footer from '../../home/components/Footer';
import WhatsAppFloat from '../../home/components/WhatsAppFloat';
import SEO from '@/components/SEO';

export default function FinancePage() {
  const { t } = useTranslation('navigation');
  const isEn = t('home') === 'Home';

  const solutions = [
    {
      titleFr: 'Gestion de Portefeuille', titleEn: 'Portfolio Management',
      descFr: 'Gestion complète des portefeuilles clients et investissements', descEn: 'Complete client portfolio and investment management',
      icone: 'ri-pie-chart-line',
      featuresFr: ['Suivi des portefeuilles clients', 'Analyse de performance en temps réel', 'Gestion des risques automatisée', 'Reporting personnalisé', 'Alertes de seuils automatiques'],
      featuresEn: ['Client portfolio tracking', 'Real-time performance analysis', 'Automated risk management', 'Customized reporting', 'Automatic threshold alerts'],
    },
    {
      titleFr: 'Conformité Réglementaire', titleEn: 'Regulatory Compliance',
      descFr: 'Respect des normes et réglementations financières', descEn: 'Compliance with financial standards and regulations',
      icone: 'ri-shield-check-line',
      featuresFr: ['Conformité aux normes locales', 'Audit trail complet', 'Rapports réglementaires automatiques', 'Gestion des risques de conformité', 'Mise à jour réglementaire continue'],
      featuresEn: ['Local standards compliance', 'Complete audit trail', 'Automatic regulatory reports', 'Compliance risk management', 'Continuous regulatory updates'],
    },
    {
      titleFr: 'CRM Financier', titleEn: 'Financial CRM',
      descFr: 'Gestion optimisée de la relation client financière', descEn: 'Optimized financial client relationship management',
      icone: 'ri-customer-service-line',
      featuresFr: ['Profils clients détaillés', 'Historique des transactions', 'Segmentation clientèle avancée', 'Campagnes marketing ciblées', 'Suivi des opportunités'],
      featuresEn: ['Detailed client profiles', 'Transaction history', 'Advanced client segmentation', 'Targeted marketing campaigns', 'Opportunity tracking'],
    },
    {
      titleFr: 'Analyse des Risques', titleEn: 'Risk Analysis',
      descFr: 'Évaluation et gestion des risques financiers', descEn: 'Assessment and management of financial risks',
      icone: 'ri-line-chart-line',
      featuresFr: ['Scoring de crédit automatisé', 'Analyse de solvabilité', 'Modèles de risque personnalisés', 'Stress testing', 'Alertes de risque temps réel'],
      featuresEn: ['Automated credit scoring', 'Solvency analysis', 'Custom risk models', 'Stress testing', 'Real-time risk alerts'],
    },
    {
      titleFr: 'Comptabilité Avancée', titleEn: 'Advanced Accounting',
      descFr: 'Comptabilité spécialisée pour les services financiers', descEn: 'Specialized accounting for financial services',
      icone: 'ri-calculator-line',
      featuresFr: ['Plan comptable spécialisé', 'Écritures automatiques', 'Réconciliation bancaire', 'États financiers réglementaires', 'Consolidation multi-entités'],
      featuresEn: ['Specialized chart of accounts', 'Automatic entries', 'Bank reconciliation', 'Regulatory financial statements', 'Multi-entity consolidation'],
    },
    {
      titleFr: 'Reporting & BI', titleEn: 'Reporting & BI',
      descFr: 'Tableaux de bord et analyses avancées', descEn: 'Dashboards and advanced analytics',
      icone: 'ri-bar-chart-line',
      featuresFr: ['Tableaux de bord temps réel', 'Analyses prédictives', 'Rapports personnalisables', 'KPI financiers automatisés', 'Alertes de performance'],
      featuresEn: ['Real-time dashboards', 'Predictive analytics', 'Customizable reports', 'Automated financial KPIs', 'Performance alerts'],
    },
  ];

  const advantages = [
    { icon: 'ri-shield-check-line', titleKey: 'financeAdv1Title', descKey: 'financeAdv1Desc' },
    { icon: 'ri-speed-up-line', titleKey: 'financeAdv2Title', descKey: 'financeAdv2Desc' },
    { icon: 'ri-shield-line', titleKey: 'financeAdv3Title', descKey: 'financeAdv3Desc' },
    { icon: 'ri-money-dollar-circle-line', titleKey: 'financeAdv4Title', descKey: 'financeAdv4Desc' },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO
        title="Logiciel Services Financiers - Alpha Solutions | CRM & Conformité"
        description="Solutions pour services financiers : gestion de portefeuille, conformité réglementaire, CRM financier, analyse des risques, comptabilité avancée et reporting BI."
        keywords="logiciel finance Cameroun, CRM financier Douala, conformité réglementaire, gestion risques financiers"
        canonicalPath="/secteurs/finance"
        schema={{
          "@context": "https://schema.org",
          "@type": "Service",
          "name": "Logiciel Services Financiers Alpha Solutions",
          "description": "Solutions pour institutions financières au Cameroun",
          "brand": { "@type": "Organization", "name": "Alpha Solutions" }
        }}
      />
      <Header />
      
      <section className="relative bg-cover bg-center py-20"
        style={{ backgroundImage: `linear-gradient(rgba(0,0,0,0.6),rgba(0,0,0,0.6)), url('https://readdy.ai/api/search-image?query=modern%20african%20financial%20services%20office%20with%20advanced%20portfolio%20management%20system%20and%20digital%20banking%20technology%2C%20professional%20financial%20institution%20with%20compliance%20and%20risk%20management%20solutions&width=1200&height=600&seq=finance-hero&orientation=landscape')` }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center text-white">
            <h1 className="text-4xl md:text-6xl font-bold mb-6">{t('financeHeroTitle')}</h1>
            <p className="text-xl md:text-2xl mb-8 max-w-3xl mx-auto">{t('financeHeroDesc')}</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="https://wa.me/237693385267" target="_blank" rel="noopener noreferrer"
                className="bg-green-600 hover:bg-green-700 text-white px-8 py-4 rounded-lg font-semibold transition-colors cursor-pointer whitespace-nowrap">
                <i className="ri-whatsapp-line mr-2"></i>{t('sectorDemoBtn')}
              </a>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">{t('financeSolutionsTitle')}</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">{t('financeSolutionsSubtitle')}</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {solutions.map((sol, index) => (
              <div key={index} className="bg-white rounded-lg shadow-lg p-6 hover:shadow-xl transition-shadow">
                <div className="w-16 h-16 bg-blue-600 text-white rounded-full flex items-center justify-center mb-4">
                  <i className={`${sol.icone} text-2xl`}></i>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">{isEn ? sol.titleEn : sol.titleFr}</h3>
                <p className="text-gray-600 mb-4">{isEn ? sol.descEn : sol.descFr}</p>
                <ul className="space-y-2">
                  {(isEn ? sol.featuresEn : sol.featuresFr).map((f, idx) => (
                    <li key={idx} className="flex items-start">
                      <i className="ri-check-line text-green-600 mr-2 mt-1"></i>
                      <span className="text-sm text-gray-700">{f}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 bg-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">{t('financeWhyTitle')}</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {advantages.map((adv, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 bg-blue-600 text-white rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className={`${adv.icon} text-2xl`}></i>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{t(adv.titleKey)}</h3>
                <p className="text-gray-600">{t(adv.descKey)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-blue-50 rounded-lg p-8 border-l-4 border-blue-500">
            <div className="flex items-center mb-6">
              <img src="https://readdy.ai/api/search-image?query=professional%20african%20financial%20advisor%20in%20modern%20banking%20office%2C%20confident%20finance%20manager%20overseeing%20successful%20financial%20services%20with%20digital%20portfolio%20management%20technology&width=100&height=100&seq=cn237-owner&orientation=squarish"
                alt={t('financeTestiName')} className="w-16 h-16 rounded-full object-cover mr-4" />
              <div>
                <h3 className="text-xl font-bold text-blue-900">{t('financeTestiName')}</h3>
                <p className="text-blue-700">{t('financeTestiRole')}</p>
              </div>
            </div>
            <blockquote className="text-lg text-blue-800 italic mb-4">"{t('financeTestiQuote')}"</blockquote>
            <div className="flex items-center">
              <div className="flex text-yellow-400">{[...Array(5)].map((_, i) => <i key={i} className="ri-star-fill"></i>)}</div>
              <span className="ml-2 text-blue-700 font-medium">{t('financeSatisfied')}</span>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-blue-900 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-4">{t('financeCtaTitle')}</h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">{t('financeCtaDesc')}</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="https://wa.me/237693385267" target="_blank" rel="noopener noreferrer"
              className="bg-green-600 hover:bg-green-700 text-white px-8 py-4 rounded-lg font-semibold transition-colors cursor-pointer whitespace-nowrap">
              <i className="ri-whatsapp-line mr-2"></i>{t('sectorFreeDemo')}
            </a>
            <a href="/solutions" className="bg-white text-blue-900 px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition-colors cursor-pointer whitespace-nowrap">
              {t('sectorViewSolutions')}
            </a>
          </div>
        </div>
      </section>

      <Footer />
      <WhatsAppFloat />
    </div>
  );
}
